import React, { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Button,
  Grid,
  Typography,
  Chip,
  alpha,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Psychology as PsychologyIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getAbcPatternById,
  updateAbcPattern,
} from "../../../../../../api/services/observations";
import { getAllLocations } from "../../../../../../api/services/settings";
import { getSubLocations } from "../../../../../../api/services/scheduling";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import {
  BEHAVIOUR_TYPES,
  RISK_LEVELS,
  SERVICE_USER_PRESENTATION,
} from "./abcpattern.config";

export const EditAbcPatternModal = ({
  show,
  handleClose,
  selectedAbcPattern,
  refetch,
}) => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({});

  const { data: abcPattern } = useQuery(
    [QK.abcPattern, selectedAbcPattern],
    () => getAbcPatternById(selectedAbcPattern),
    { enabled: !!selectedAbcPattern }
  );

  const { data: locations } = useQuery([QK.locations], getAllLocations);

  const { data: sublocations } = useQuery(
    [QK.subLocations, formData.location],
    () => getSubLocations(formData.location),
    { enabled: !!formData.location }
  );

  useEffect(() => {
    if (abcPattern) setFormData(abcPattern);
  }, [abcPattern]);

  const editAbcPatternMutation = useMutation(
    (newData) => updateAbcPattern(selectedAbcPattern, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("ABC Pattern updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.abcPattern]);
        handleClose();
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to update ABC Pattern",
          { variant: "error" }
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      location: locationId,
      area: "",
    }));
  };

  const handleRiskLevelSelect = (level) => {
    setFormData((prev) => ({ ...prev, risk_level: level }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData, client: clientId };
    editAbcPatternMutation.mutate(submitData);
  };

  const locationOptions = (locations || []).map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  const areaOptions = (sublocations || []).map((sub) => ({
    value: sub.id,
    label: sub.name,
  }));

  const behaviourTypeOptions = BEHAVIOUR_TYPES.map((opt) => ({
    value: opt.value,
    label: opt.name,
  }));

  const presentationOptions = SERVICE_USER_PRESENTATION.map((opt) => ({
    value: opt.value,
    label: opt.name,
  }));

  const isLoading = editAbcPatternMutation.isLoading;

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(16, 24, 40, 0.18)",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)",
          borderBottom: "1px solid #E4E7EC",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.16
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <PsychologyIcon
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
          </Box>
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "#101828", lineHeight: 1.2 }}
              >
                Edit ABC Pattern
              </Typography>
              <Chip
                label="Editing"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  bgcolor: "#E0EAFF",
                  color: "#2563EB",
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "#667085", fontSize: "0.72rem" }}
            >
              Update behaviour observation and risk assessment
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: "#667085",
            bgcolor: "#FFFFFF",
            border: "1px solid #E4E7EC",
            "&:hover": { bgcolor: "#F9FAFB" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          px: 3,
          py: 3,
          maxHeight: "72vh",
          overflowY: "auto",
          bgcolor: "#FFFFFF",
        }}
      >
        <Grid container spacing={2.25}>
          <Grid size={{ xs: 12 }}>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#667085",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              Incident Details
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomDateTime
              name="date_time"
              label="Date / Time *"
              value={formData.date_time || ""}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomSelect
              name="location"
              label="Location *"
              value={formData.location || ""}
              onChange={handleLocationChange}
              options={locationOptions}
              placeholder="Select location"
              required
              fullWidth
              size="small"
              searchable={true}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomSelect
              name="area"
              label="Area *"
              value={formData.area || ""}
              onChange={handleChange}
              options={areaOptions}
              placeholder="Select area"
              required
              fullWidth
              size="small"
              searchable={true}
              disabled={!formData.location}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomSelect
              name="behaviour_type"
              label="Behaviour Type *"
              value={formData.behaviour_type || ""}
              onChange={handleChange}
              options={behaviourTypeOptions}
              placeholder="Select behaviour type"
              required
              fullWidth
              size="small"
              searchable={true}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomSelect
              name="service_user_presentation"
              label="Service User Presentation"
              value={formData.service_user_presentation || ""}
              onChange={handleChange}
              options={presentationOptions}
              placeholder="Select presentation"
              fullWidth
              size="small"
              searchable={true}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomInput
              type="text"
              name="who_else_present"
              label="Who else was present?"
              value={formData.who_else_present || ""}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                mt: 1,
                mb: 1.5,
                pt: 2,
                borderTop: "1px dashed #E4E7EC",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#667085",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              ABC Observation
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="antecedent"
              label="Antecedent *"
              value={formData.antecedent || ""}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="behaviour"
              label="Behaviour *"
              value={formData.behaviour || ""}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="consequences"
              label="Consequences *"
              value={formData.consequences || ""}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="action_taken"
              label="Action Taken *"
              value={formData.action_taken || ""}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="what_they_said"
              label="What did they say during the incident?"
              value={formData.what_they_said || ""}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomInput
              type="text"
              name="other_appearance_details"
              label="Other appearance details"
              value={formData.other_appearance_details || ""}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomInput
              type="number"
              name="behaviour_duration"
              label="Behaviour Duration (mins)"
              value={formData.behaviour_duration || ""}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomInput
              type="number"
              name="situation_duration"
              label="Situation Duration (mins)"
              value={formData.situation_duration || ""}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={12}>
            <Box
              sx={{
                mt: 1,
                mb: 1.5,
                pt: 2,
                borderTop: "1px dashed #E4E7EC",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#667085",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              Risk Assessment
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {RISK_LEVELS.map(({ value, color }) => {
                const isActive = formData.risk_level === value;
                return (
                  <Button
                    key={value}
                    variant={isActive ? "contained" : "outlined"}
                    color={color}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      minWidth: 90,
                      height: 34,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: "0.02em",
                    }}
                    onClick={() => handleRiskLevelSelect(value)}
                  >
                    {value}
                  </Button>
                );
              })}
            </Box>
          </Grid>

          <Grid size={12}>
            <Box
              sx={{
                mt: 1,
                mb: 1.5,
                pt: 2,
                borderTop: "1px dashed #E4E7EC",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#667085",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 1.5,
              }}
            >
              Distribution
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                alignItems: "center",
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                border: "1px solid #E4E7EC",
                bgcolor: "#F9FAFB",
              }}
            >
              <CustomCheckbox
                label="Share with Manager"
                name="manager"
                checked={formData.manager || false}
                onClick={(checked) =>
                  handleChange({
                    target: { name: "manager", type: "checkbox", checked },
                  })
                }
              />
              <CustomCheckbox
                label="Handover"
                name="handover"
                checked={formData.handover || false}
                onClick={(checked) =>
                  handleChange({
                    target: { name: "handover", type: "checkbox", checked },
                  })
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #E4E7EC",
          bgcolor: "#FAFBFC",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            py: 0.9,
            fontSize: "0.82rem",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon fontSize="small" />
            )
          }
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            bgcolor: "#2469F0",
            px: 3.5,
            py: 0.9,
            fontSize: "0.82rem",
            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0 4px 12px rgba(36, 105, 240, 0.25)",
            },
          }}
        >
          {isLoading ? "Updating..." : "Update ABC Pattern"}
        </Button>
      </Box>
    </Dialog>
  );
};